import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Shield, Search, AlertTriangle, CheckCircle, Clock, ExternalLink, Mail, Send, X, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function DarkWebMonitoring() {
  const [loading, setLoading] = useState(false);
  const [scans, setScans] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [domain, setDomain] = useState('');
  const [currentScan, setCurrentScan] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', name: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sentReportUrl, setSentReportUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dark-web/scans`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (error) {
      toast.error('Failed to load scans');
    }
  };

  const handleScan = async () => {
    if (!domain) {
      toast.error('Please enter a domain to scan');
      return;
    }

    setScanning(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/dark-web/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ domain })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Scan completed successfully');
        setCurrentScan(data);
        setDomain('');
        fetchScans();
      } else {
        throw new Error('Scan failed');
      }
    } catch (error) {
      toast.error('Failed to initiate scan');
    } finally {
      setScanning(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.email) {
      toast.error('Please enter a recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/dark-web/send-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          scan_id: currentScan.scan_id,
          recipient_email: emailForm.email,
          recipient_name: emailForm.name,
          custom_message: emailForm.message
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Report link generated for ${emailForm.email}`);
        setSentReportUrl(data.view_url);
        setEmailForm({ email: '', name: '', message: '' });
      } else {
        throw new Error('Failed to send report');
      }
    } catch (error) {
      toast.error('Failed to generate report link');
    } finally {
      setSendingEmail(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'in_progress':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'failed':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  return (
    <DashboardLayout title="Dark Web Monitoring">
      <div className="space-y-6" data-testid="dark-web-monitoring-page">
        {/* Header with Info */}
        <div className="bg-gradient-to-r from-destructive/10 to-destructive/5 border border-destructive/20 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-destructive/20 flex items-center justify-center flex-shrink-0">
              <Shield className="h-6 w-6 text-destructive" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-mono font-bold mb-2">Dark Web & Breach Monitoring</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Scan your domains for compromised credentials, breached data, and dark web exposure. 
                Results are checked against Have I Been Pwned database containing billions of compromised accounts.
              </p>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  Email Breach Detection
                </span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  Password Leaks
                </span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  Stealer Logs
                </span>
                <span className="px-3 py-1 bg-background rounded-full border border-border">
                  Data Dumps
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scan Initiation */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-mono font-bold mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Scan Your Domain
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Enter your company domain to check for compromised email addresses and exposed credentials
          </p>
          
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="domain" className="font-mono mb-2 block">Domain to Scan</Label>
              <Input
                id="domain"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="font-mono"
                data-testid="domain-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleScan}
                disabled={scanning || !domain}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono"
                data-testid="scan-button"
              >
                {scanning ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Scan Now
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Current Scan Results */}
        {currentScan && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Latest Scan Results</h3>
              {currentScan.breached_emails_found > 0 && (
                <Button
                  onClick={() => setShowEmailModal(true)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Report to User
                </Button>
              )}
            </div>
            <ScanResults scan={currentScan} />
          </div>
        )}

        {/* Email Modal */}
        {showEmailModal && currentScan && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <h3 className="font-bold text-lg">Send Breach Report</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  setShowEmailModal(false);
                  setSentReportUrl(null);
                }} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="p-4 space-y-4">
                {!sentReportUrl ? (
                  <>
                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                      <p className="text-sm text-destructive font-medium">
                        ⚠ {currentScan.breached_emails_found} breach(es) found for {currentScan.domain}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="recipientEmail">Recipient Email *</Label>
                      <Input
                        id="recipientEmail"
                        type="email"
                        placeholder={`user@${currentScan.domain}`}
                        value={emailForm.email}
                        onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Send to someone at the affected organization
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="recipientName">Recipient Name (Optional)</Label>
                      <Input
                        id="recipientName"
                        placeholder="John Smith"
                        value={emailForm.name}
                        onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowEmailModal(false)} 
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSendEmail} 
                        disabled={sendingEmail || !emailForm.email}
                        className="flex-1 bg-primary"
                      >
                        {sendingEmail ? (
                          <>
                            <Clock className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Generate Link
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
                      <CheckCircle className="h-12 w-12 text-primary mx-auto mb-3" />
                      <h4 className="font-bold mb-2">Report Link Generated!</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Share this link with {emailForm.email || 'the recipient'} so they can view their breach report.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Report Link</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={sentReportUrl} 
                          readOnly 
                          className="text-xs"
                        />
                        <Button
                          variant="outline"
                          onClick={() => copyToClipboard(sentReportUrl)}
                          className="flex-shrink-0"
                        >
                          {copied ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        This link expires in 30 days
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowEmailModal(false);
                          setSentReportUrl(null);
                        }} 
                        className="flex-1"
                      >
                        Close
                      </Button>
                      <Button 
                        onClick={() => {
                          const mailtoLink = `mailto:${emailForm.email}?subject=Dark Web Breach Report for ${currentScan.domain}&body=Hi${emailForm.name ? ' ' + emailForm.name : ''},%0D%0A%0D%0AWe have detected potential data breaches affecting your organization's domain (${currentScan.domain}).%0D%0A%0D%0APlease review your breach report here:%0D%0A${encodeURIComponent(sentReportUrl)}%0D%0A%0D%0AThis link will expire in 30 days.%0D%0A%0D%0ABest regards,%0D%0AXentra Security Team`;
                          window.location.href = mailtoLink;
                        }}
                        className="flex-1 bg-primary"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Open Email Client
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scan History */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-mono font-bold mb-4">Scan History</h3>
          {scans.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No scans performed yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => (
                <div
                  key={scan.scan_id}
                  className="p-4 bg-background border border-border rounded-md hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => setCurrentScan(scan)}
                  data-testid={`scan-item-${scan.scan_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono font-bold">{scan.domain}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-mono border ${getStatusColor(scan.status)}`}>
                          {scan.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
                        <span>Checked: {scan.total_emails_checked} emails</span>
                        {scan.breached_emails_found > 0 && (
                          <span className="text-destructive font-bold">
                            ⚠ {scan.breached_emails_found} breaches found
                          </span>
                        )}
                        <span>{new Date(scan.started_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function ScanResults({ scan }) {
  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-background border border-border rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-primary" />
            <span className="text-sm font-mono text-muted-foreground">Emails Checked</span>
          </div>
          <p className="text-3xl font-mono font-bold">{scan.total_emails_checked}</p>
        </div>

        <div className="bg-background border border-border rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span className="text-sm font-mono text-muted-foreground">Breaches Found</span>
          </div>
          <p className="text-3xl font-mono font-bold text-destructive">{scan.breached_emails_found}</p>
        </div>

        <div className="bg-background border border-border rounded-md p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-accent" />
            <span className="text-sm font-mono text-muted-foreground">Scan Time</span>
          </div>
          <p className="text-xl font-mono font-bold">
            {scan.completed_at 
              ? `${Math.round((new Date(scan.completed_at) - new Date(scan.started_at)) / 1000)}s`
              : 'In progress'}
          </p>
        </div>
      </div>

      {/* Breach Details */}
      {scan.breaches_found && scan.breaches_found.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-md font-mono font-bold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Discovered Breaches
          </h4>
          {scan.breaches_found.map((breach, index) => (
            <div key={index} className="bg-destructive/5 border border-destructive/20 rounded-md p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h5 className="font-mono font-bold text-lg">{breach.Title}</h5>
                  <p className="text-sm text-muted-foreground font-mono">
                    {breach.Domain} • Breach Date: {new Date(breach.BreachDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-mono font-bold text-destructive">
                    {(breach.PwnCount / 1000000).toFixed(1)}M
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">accounts affected</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {breach.DataClasses.map((dataClass, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-background rounded-md text-xs font-mono border border-border"
                  >
                    {dataClass}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {scan.breached_emails_found === 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-md p-6 text-center">
          <CheckCircle className="h-12 w-12 text-primary mx-auto mb-3" />
          <h4 className="font-mono font-bold text-lg mb-2">No Breaches Detected</h4>
          <p className="text-sm text-muted-foreground">
            Great news! No compromised credentials were found for this domain.
          </p>
        </div>
      )}
    </div>
  );
}

export default DarkWebMonitoring;
